import {View, Text} from 'react-native';
import React, {useState} from 'react';
import {useStyle} from 'src/providers/style';
import {useDimensions} from '@react-native-community/hooks';

import {
  calculateCancellationRate,
  cancellationReasonMap,
} from 'src/common-utils/cancellation-rate';
import {Colors, Typography} from 'src/styles';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLabel,
  VictoryStack,
  VictoryTheme,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory-native';
import {ForeignObject, G} from 'react-native-svg';
import {boxShadow} from 'src/styles/mixins';
import moment from 'moment';
import CancellationLegend from 'src/modules/scheduling/components/cancellation-legend';
import {Separator} from 'src/common-components/atoms';
import InformationTooltip from 'src/modules/programs/components/information-tooltip';

const tooltipText = `Total weekly cancellations, grouped by reason, with counts and percentages.
Example: 14 cancellations → 4 Client-Initiated (29%), 2 Added-in-Error (14%)`;

const HTMLFlyOut = ({x, y, datum, data}: any) => {
  const styles = useStyle();
  const weekData = data[datum._group];

  return (
    <G>
      <ForeignObject
        x={x - 70}
        y={y / 2}
        width="145"
        height="600"
        /* eslint-disable-next-line react-native/no-inline-styles */
        style={{overflow: 'visible'}}>
        <View
          style={[
            styles.borderRadius20,
            styles.backgroundColorWhite,
            styles.paddingL,
            boxShadow(Colors.RAVEN_BLACK),
          ]}>
          <View style={[styles.paddingLBottom]}>
            <Text style={[Typography.CAPTION_MEDIUM]}>Week of {datum.x}:</Text>
          </View>
          <View style={[styles.paddingSMBottom]}>
            <Text style={[Typography.CAPTION_MEDIUM]}>Total Cancellations</Text>
            <Text style={[Typography.CAPTION, styles.paddingSMBottom]}>
              {weekData?.total || 0}
            </Text>
          </View>
          {Object.keys(cancellationReasonMap).map(reasonKey =>
            weekData.counts[reasonKey] > 0 ? (
              <View style={[styles.paddingSMVertical]} key={reasonKey}>
                <Text style={[Typography.CAPTION_MEDIUM]}>
                  {cancellationReasonMap[reasonKey]?.title}
                </Text>
                <Text style={[Typography.CAPTION]}>
                  {weekData.counts[reasonKey] || 0}
                  {weekData.counts[reasonKey] > 0
                    ? ` (${weekData.percentages[reasonKey] || 0}%)`
                    : null}
                </Text>
              </View>
            ) : null,
          )}
        </View>
      </ForeignObject>
    </G>
  );
};

const CancellationRate = ({appointments}: any) => {
  const styles = useStyle();
  const dimensions = useDimensions();
  const [hoveredIndex, setHoveredIndex] = useState<any>();

  const data = calculateCancellationRate(appointments);
  const maxTotal = data.length ? Math.max(...data.map(d => d.total)) : 0;
  const domain = {x: [0, data.length + 1], y: [0, Math.max(maxTotal, 1)]};

  return (
    <View style={[styles.flex, styles.card, styles.padding]}>
      <>
        <View style={[styles.row]}>
          <Text style={[Typography.P2_BOLD]}>Cancellation Rate</Text>
          <Separator width={4} />
          <InformationTooltip value={tooltipText} />
        </View>
        <Text style={[Typography.P3, styles.alignSelfCenter]}>
          Cancelled Appointments by Reason
        </Text>
        <VictoryChart
          theme={VictoryTheme.material}
          width={dimensions.screen.width * 0.95}
          domain={domain}
          domainPadding={{x: 40}}
          scale={{x: 'time'}}
          containerComponent={
            <VictoryVoronoiContainer
              onActivated={points => {
                const key = points[0]?.eventKey;
                if (key !== undefined) {
                  setHoveredIndex(key);
                }
              }}
              onDeactivated={() => setHoveredIndex(null)}
            />
          }>
          <VictoryAxis
            tickFormat={t => moment(t, 'MM/DD/YY').format('MM/DD/YY')}
            style={{
              axis: {stroke: Colors.GRAY_100},
              tickLabels: {
                ...Typography.CAPTION,
                angle: 0,
                fill: Colors.PRIMARY_600,
              },
              grid: {stroke: 'transparent'},
            }}
            label={'Week Of'}
            axisLabelComponent={
              <VictoryLabel
                style={{...Typography.CAPTION, color: Colors.TEXT_PRIMARY}}
                dy={30}
              />
            }
            tickLabelComponent={
              <VictoryLabel
                style={{...Typography.CAPTION, fill: Colors.TEXT_PRIMARY}}
              />
            }
          />
          <VictoryAxis dependentAxis tickFormat={x => `${x}`} />
          <VictoryStack>
            {Object.keys(cancellationReasonMap).map(reasonKey => (
              <VictoryBar
                key={reasonKey}
                data={data.map(d => ({
                  x: moment(d.week).format('MM/DD/YY'),
                  y: d.counts[reasonKey],
                }))}
                style={{
                  data: {fill: cancellationReasonMap[reasonKey].color},
                  labels: {
                    fill: Colors.PRIMARY_600,
                    ...Typography.CAPTION,
                  },
                }}
                barWidth={30}
              />
            ))}
          </VictoryStack>
          <VictoryBar
            data={data.map((d, index) => ({
              x: moment(d.week).format('MM/DD/YY'),
              y: d.total / 2,
              _group: index,
              _y0: 0,
              _y1: d.total,
            }))}
            barWidth={30}
            style={{data: {fill: 'transparent'}}}
            labels={({datum}) =>
              String(hoveredIndex) === String(datum._group) ? ' ' : null
            }
            labelComponent={
              <VictoryTooltip
                renderInPortal
                flyoutComponent={<HTMLFlyOut data={data} />}
              />
            }
          />
        </VictoryChart>
        <View style={[styles.paddingBottom]}>
          <CancellationLegend />
        </View>
      </>
    </View>
  );
};

export default CancellationRate;
